import { Box, Typography, Button, Tooltip } from "@mui/material";
import React, { useRef, useEffect, useCallback, useState } from "react";
import { Phrase, PhraseStatus, PhraseType } from "../../../api/phrasebook";
import { AutoAwesome, Circle, DragIndicator, Info } from "@mui/icons-material";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import usePhrasebookStore from "./store";
import { phraseColorByStatus } from "./colors";
import { processPhrasesValidation } from "../limits-processor";
import {
  BrandBatchSettings,
  ContentGroup,
} from "../../../pages/playground/types";
import { useAuthStore } from "../../../authProvider";
import { getPhraseTypeLabel } from "../../../utils/fieldNames";
import FormattableInput from "../../common/formattable-input";
import { preserveTagsTransform } from "../../../utils/phrase-formatting";
import { toPhraseLimits } from "../../../utils/content-groups";
import {
  getBulletPrefixDisplay,
  getPhraseWrapDisplay,
  getParagraphWrapDisplay,
  getListWrapDisplay,
  hasPhrasePrefix,
  compactTagAnnotation,
} from "../../../utils/content-group-formatting";

export default function PhrasesManager({
  type,
  phrases,
  batchSettings,
  contentGroup,
  wordsToAvoid,
  updatePhrases,
  readOnly,
  hasBulletPoints,
  isAIEnabled,
}: {
  type: PhraseType;
  phrases: Phrase[];
  batchSettings: BrandBatchSettings;
  contentGroup: ContentGroup;
  wordsToAvoid?: string[];
  updatePhrases: (phrases: Phrase[]) => void;
  readOnly?: boolean;
  hasBulletPoints?: boolean;
  isAIEnabled?: boolean;
}) {
  const { setHasChanges, savingPhrases } = usePhrasebookStore();

  const {
    jobId,
    activeField,
    searchTab,
    setQuery,
    selectedPhrase,
    setSelectedPhrase,
    garmentType,
    garmentTypeApproved,
    setIsGenerating,
    startPhrasebookSearch,
  } = usePhrasebookStore();

  const anchorRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const emptyStateRef = useRef<{ [key: number]: boolean }>({});

  const settings = toPhraseLimits(contentGroup);

  const minPhrases = settings?.minPhrases || 0;
  const maxPhrases = settings?.maxPhrases || 0;

  // Update refs array when maxPhrases changes
  useEffect(() => {
    anchorRefs.current = Array(maxPhrases).fill(null);
  }, [maxPhrases]);

  // Stable ref for updatePhrases to avoid re-render loops in useEffect
  const updatePhrasesRef = useRef(updatePhrases);
  updatePhrasesRef.current = updatePhrases;

  // Update local phrases and initialize refs
  useEffect(() => {
    if (phrases.length >= maxPhrases) return;

    // Initialize emptyFieldRef based on phrases
    phrases.forEach((phrase, index) => {
      emptyStateRef.current[index] =
        !phrase.phrase || phrase.phrase.trim() === "";
    });

    // Initialize missing phrases
    if (phrases.length < maxPhrases) {
      const currentPhrases = [...phrases];

      // Create empty phrases for missing indexes
      for (let i = 0; i < maxPhrases; i++) {
        if (!currentPhrases[i]) {
          currentPhrases[i] = {
            phrase: "", // Empty phrase content
            type, // Use the current phrase type
            status: undefined, // No status for empty phrases
            order: i + 1, // Order is 1-based index
          };
        }
      }

      // Update the phrases via parent component
      updatePhrasesRef.current(currentPhrases);
    }
  }, [phrases, maxPhrases, type]);

  // Function to handle input changes and trigger suggestion search
  const handleInputChange = useCallback(
    (
      value: string,
      index: number,
      status?: PhraseStatus,
      forceReload: boolean = true
    ) => {
      startPhrasebookSearch({ index, type }, value);

      const isEmptyValue = !value || value.trim() === "";

      if (forceReload) {
        if (isEmptyValue) {
          emptyStateRef.current[index] = true;
          setQuery(garmentType);
        } else {
          setQuery(value);
        }
      }

      const newPhrases = [...phrases];

      const oldPhrase = phrases[index];

      // provided status, otherwise un-reviewed
      let phraseStatus: PhraseStatus | undefined =
        status || PhraseStatus.UNREVIEWED;

      if (isEmptyValue) {
        phraseStatus = undefined;
      }

      newPhrases[index] = {
        ...oldPhrase,
        phrase: value,
        type,
        status: phraseStatus,
      };

      updatePhrases(newPhrases);

      // Track changes for the save button
      setHasChanges(true);
    },
    [
      phrases,
      type,
      setQuery,
      updatePhrases,
      setHasChanges,
      garmentType,
      startPhrasebookSearch,
    ]
  );

  useEffect(() => {
    const isCurrentActiveField = activeField?.type === type;

    if (selectedPhrase && isCurrentActiveField) {
      handleInputChange(
        selectedPhrase.phrase,
        activeField?.index || 0,
        selectedPhrase.status,
        false
      );

      setSelectedPhrase(null);
    }
  }, [selectedPhrase, setSelectedPhrase, activeField, type, handleInputChange]);

  const processedLimits = processPhrasesValidation(
    phrases,
    settings,
    wordsToAvoid
  );

  // Track recently swapped phrases to apply blink animation
  const [recentlySwappedIndexes, setRecentlySwappedIndexes] = useState<
    number[]
  >([]);

  const hasTitleTemplates = (batchSettings?.title_templates || []).length > 0;

  const getTitleGenerationTooltip = () => {
    if (!hasTitleTemplates) {
      return "You need to add title templates to generate titles. Have a look at the batch settings page.";
    }

    if (!garmentType) {
      return "You need a garment type to generate titles. Have a look at the Functional Bullet Points.";
    }

    if (!garmentTypeApproved) {
      return "You must approve the garment type by clicking on it making it green.";
    }
    return "";
  };

  const canGenerateTitle =
    hasTitleTemplates && garmentType && garmentTypeApproved;

  // Drag & drop handler
  const handleDrop = useCallback(
    (from: string, to: string) => {
      // Prevent drag operations while save is in progress to avoid index corruption
      if (savingPhrases) return;

      const fromIndex = Number(from);
      const toIndex = Number(to);

      // Don't do anything if the same position
      if (fromIndex === toIndex) return;

      const newPhrases = [...phrases];

      // Get the phrase being moved
      const movedPhrase = Object.assign({}, newPhrases[fromIndex]);

      // Remove the phrase from its original position
      newPhrases.splice(fromIndex, 1);

      // Insert the phrase at the target position
      newPhrases.splice(toIndex, 0, movedPhrase);

      // In the original array, after removing the phrase
      const originalArray = [...phrases];
      originalArray.splice(fromIndex, 1);

      // Determine which element got displaced by the insertion
      let displacedElement = null;

      if (toIndex < fromIndex) {
        // Moving up: the element that was at toIndex gets pushed down
        displacedElement = originalArray[toIndex];
      } else {
        // Moving down: when moving down, we need to adjust the target index
        // since removing the original item shifts all indexes
        const adjustedToIndex = toIndex - 1;
        displacedElement = originalArray[adjustedToIndex];
      }

      // Find the new index of the displaced element in the updated array
      const displacedIndex = displacedElement
        ? newPhrases.indexOf(displacedElement)
        : -1;

      // Set the affected indices for animation
      const affectedIndices = [
        toIndex, // The new position of the moved item
        displacedIndex, // The displaced item's new position
      ].filter((idx) => idx >= 0 && idx < newPhrases.length);

      setRecentlySwappedIndexes(affectedIndices);

      // Clear the recently swapped state after animation
      setTimeout(() => {
        setRecentlySwappedIndexes([]);
      }, 1000);

      updatePhrases(newPhrases);
      setHasChanges(true);
    },
    [phrases, savingPhrases, updatePhrases, setHasChanges]
  );

  const isKV = contentGroup.formatting.keyValueMode;
  const kvParagraphWrap = getParagraphWrapDisplay(contentGroup.formatting);
  const kvListWrap = getListWrapDisplay(contentGroup.formatting);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <Typography variant="h6">
              {getPhraseTypeLabel(type, batchSettings)}
            </Typography>

            {isAIEnabled && (
              <>
                {contentGroup.kind === "title" && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {!canGenerateTitle && (
                      <Tooltip title={getTitleGenerationTooltip()}>
                        <Info
                          sx={{ color: "primary.main", fontSize: "1.1rem" }}
                        />
                      </Tooltip>
                    )}
                    <Button
                      variant="text"
                      color="success"
                      disabled={!canGenerateTitle}
                      onClick={() => {
                        setIsGenerating({ type: "title", ts: Date.now() });
                      }}
                    >
                      <AutoAwesome sx={{ mr: 1, fontSize: "0.9rem" }} />
                      AI Generate
                    </Button>
                  </Box>
                )}

                {contentGroup.kind === "description" && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Tooltip
                      title={
                        !hasBulletPoints
                          ? "You need at least one functional bullet point to generate with AI"
                          : ""
                      }
                    >
                      <span>
                        <Button
                          variant="text"
                          color="success"
                          disabled={!hasBulletPoints}
                          onClick={() => {
                            let generationType:
                              | "regularDescription"
                              | "shortDescription"
                              | "longDescription" = "regularDescription";
                            if (contentGroup.id === "SHORT_DESCRIPTION") {
                              generationType = "shortDescription";
                            } else if (contentGroup.id === "LONG_DESCRIPTION") {
                              generationType = "longDescription";
                            }
                            setIsGenerating({
                              type: generationType,
                              ts: Date.now(),
                            });
                          }}
                        >
                          <AutoAwesome sx={{ mr: 1, fontSize: "0.9rem" }} />
                          AI Generate
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </>
            )}
          </Box>

          <Box
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              {processedLimits.hasPhrasesConstraints && (
                <>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: processedLimits.needsToReachMinimumPhrases
                        ? "error.main"
                        : "success.main",
                    }}
                  >
                    {minPhrases}-{maxPhrases} phrases
                  </Typography>
                </>
              )}
              {processedLimits.hasCharactersConstraints && (
                <>
                  {" · "}
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color:
                        processedLimits.needsToReachMinimumCharacters ||
                        processedLimits.exceededMaxCharacters
                          ? "error.main"
                          : "success.main",
                    }}
                  >
                    {settings?.minCharacters}-{settings?.maxCharacters}{" "}
                    characters
                  </Typography>
                </>
              )}
              {processedLimits.hasWordsConstraints && (
                <>
                  {" · "}
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color:
                        processedLimits.needsToReachMinimumWords ||
                        processedLimits.exceededMaxWords
                          ? "error.main"
                          : "success.main",
                    }}
                  >
                    {settings?.minWords}-{settings?.maxWords} words
                  </Typography>
                </>
              )}
              {" · "}
              <Typography
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                {processedLimits.charactersCount} characters,{" "}
                {processedLimits.wordsCount} words
              </Typography>
            </Box>

            {processedLimits.forbiddenWords.length > 0 && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "error.main",
                }}
              >
                forbidden words: {processedLimits.forbiddenWords.join(", ")}
              </Typography>
            )}
          </Box>
        </Box>

        {isKV ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {/* Group-level opening tags for KV mode */}
            {kvParagraphWrap.openingTag && (
              <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#666", pl: 1 }}>
                {compactTagAnnotation(kvParagraphWrap.openingTag)}
              </Typography>
            )}
            {kvListWrap.openingTag && (
              <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#666", pl: 1 }}>
                {compactTagAnnotation(kvListWrap.openingTag)}
              </Typography>
            )}

            {/* KV column headers */}
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
                Key{contentGroup.formatting.keyWrapTag ? ` (${compactTagAnnotation(contentGroup.formatting.keyWrapTag)})` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
                Value
              </Typography>
            </Box>
            {Array.from({ length: Math.ceil(maxPhrases / 2) }).map((_, pi) => {
              const keyIdx = pi * 2;
              const valIdx = pi * 2 + 1;
              return (
                <Box key={pi} sx={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <DraggablePhrase
                      contentGroup={contentGroup}
                      index={keyIdx}
                      phrase={phrases[keyIdx]}
                      readOnly={readOnly}
                      isRecentlySwapped={recentlySwappedIndexes.includes(keyIdx)}
                      active={
                        activeField?.index === keyIdx && activeField?.type === type
                          ? keyIdx
                          : null
                      }
                      setActive={() => {
                        if (searchTab === "jobs") return;
                        startPhrasebookSearch({ index: keyIdx, type }, phrases[keyIdx]?.phrase || "");
                      }}
                      handleChange={handleInputChange}
                      handleDrop={handleDrop}
                      disabled={savingPhrases || !jobId}
                      maxPhrases={maxPhrases}
                    />
                  </Box>
                  {valIdx < maxPhrases && (
                    <Box sx={{ flex: 1 }}>
                      <DraggablePhrase
                        contentGroup={contentGroup}
                        index={valIdx}
                        phrase={phrases[valIdx]}
                        readOnly={readOnly}
                        isRecentlySwapped={recentlySwappedIndexes.includes(valIdx)}
                        active={
                          activeField?.index === valIdx && activeField?.type === type
                            ? valIdx
                            : null
                        }
                        setActive={() => {
                          if (searchTab === "jobs") return;
                          startPhrasebookSearch({ index: valIdx, type }, phrases[valIdx]?.phrase || "");
                        }}
                        handleChange={handleInputChange}
                        handleDrop={handleDrop}
                        disabled={savingPhrases || !jobId}
                        maxPhrases={maxPhrases}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Group-level closing tags for KV mode */}
            {kvListWrap.closingTag && (
              <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#666", pl: 1 }}>
                {compactTagAnnotation(kvListWrap.closingTag)}
              </Typography>
            )}
            {kvParagraphWrap.closingTag && (
              <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#666", pl: 1 }}>
                {compactTagAnnotation(kvParagraphWrap.closingTag)}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {Array.from({ length: maxPhrases }).map((_, index) => (
              <DraggablePhrase
                contentGroup={contentGroup}
                key={index}
                index={index}
                phrase={phrases[index]}
                readOnly={readOnly}
                isRecentlySwapped={recentlySwappedIndexes.includes(index)}
                active={
                  activeField?.index === index && activeField?.type === type
                    ? index
                    : null
                }
                setActive={() => {
                  if (searchTab === "jobs") return;
                  startPhrasebookSearch(
                    { index, type },
                    phrases[index]?.phrase || ""
                  );
                }}
                handleChange={handleInputChange}
                handleDrop={handleDrop}
                disabled={savingPhrases || !jobId}
                maxPhrases={maxPhrases}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

const DraggablePhrase = React.memo(function DraggablePhrase({
  contentGroup,
  index,
  phrase,
  active,
  setActive,
  handleChange,
  handleDrop,
  disabled = false,
  isRecentlySwapped = false,
  readOnly,
  maxPhrases,
}: {
  contentGroup: ContentGroup;
  index: number;
  phrase: Phrase;
  active: number | null;
  setActive: (index: number) => void;
  handleChange: (value: string, index: number) => void;
  handleDrop: (fromId: string, toId: string) => void;
  disabled?: boolean;
  isRecentlySwapped?: boolean;
  readOnly?: boolean;
  maxPhrases?: number;
}) {
  const { user } = useAuthStore();
  const ref = useRef(null);
  const [dragInfo, setDragInfo] = useState<{
    isDraggedOver: boolean;
    dragSourceIndex: number | null;
  }>({
    isDraggedOver: false,
    dragSourceIndex: null,
  });
  const [isSelectedForDragging, setIsSelectedForDragging] = useState(false);

  const phraseStatus = phrase?.status;

  const phraseColor = phraseStatus
    ? phraseColorByStatus[phraseStatus]
    : phraseColorByStatus["empty"];

  // Formatting derived from contentGroup
  const hasPrefix = hasPhrasePrefix(contentGroup.formatting);
  const bulletPrefixInfo = getBulletPrefixDisplay(contentGroup.formatting);
  const phraseWrapInfo = getPhraseWrapDisplay(contentGroup.formatting);
  const paragraphWrapInfo = getParagraphWrapDisplay(contentGroup.formatting);
  const listWrapInfo = getListWrapDisplay(contentGroup.formatting);
  const isKV = contentGroup.formatting.keyValueMode;
  // In KV mode, value phrases (odd index) are plain text — phraseWrapTag and
  // keyWrapTag wrap only the key (matches formatPhrases output, see
  // content-group-formatting.ts:158).
  const isKVValue = isKV && index % 2 === 1;

  // KV-aware placeholder
  const placeholder = isKV
    ? index % 2 === 0
      ? `Key ${Math.floor(index / 2) + 1}`
      : `Value ${Math.floor(index / 2) + 1}`
    : `Phrase ${index + 1}`;

  // Whether there's a left annotation box (bullet prefix or phrase wrap opening)
  const hasLeftAnnotation =
    !isKVValue &&
    ((hasPrefix && !!bulletPrefixInfo.text) || !!phraseWrapInfo.openingTag);

  // Set up the element as a drag source
  const dragHandleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = dragHandleRef.current;

    if (el && !disabled && !isKV) {
      return draggable({
        element: el,
        getInitialData: () => {
          return {
            id: index,
          };
        },
        onDragStart: () => setIsSelectedForDragging(true),
        onDrop: () => setIsSelectedForDragging(false),
      });
    }
  }, [dragHandleRef, index, disabled, isKV]);

  useEffect(() => {
    const el = ref.current;

    if (el && !disabled && !isKV) {
      return dropTargetForElements({
        element: el,
        onDragEnter: (event) => {
          // Get the source index from the drag event
          const sourceIndex = parseInt(event.source.data.id as string, 10);
          setDragInfo({
            isDraggedOver: true,
            dragSourceIndex: sourceIndex,
          });
        },
        onDragLeave: () => {
          setDragInfo({
            isDraggedOver: false,
            dragSourceIndex: null,
          });
        },
        onDrop: (el) => {
          setDragInfo({
            isDraggedOver: false,
            dragSourceIndex: null,
          });
          handleDrop(el.source.data.id as string, index.toString());
        },
      });
    }
  }, [ref, index, handleDrop, disabled, isKV]);

  // Create reference to the input element
  const textareaRef = useRef<HTMLDivElement>(null);

  // Function to capitalize first letter and make the rest lowercase
  const capitalizeFirstLetter = () => {
    if (!phrase?.phrase) return;
    const newText = preserveTagsTransform(phrase.phrase, (text) =>
      text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    );
    handleChange(newText, index);
  };

  // Function to make all text lowercase
  const makeAllLowercase = () => {
    if (!phrase?.phrase) return;
    handleChange(preserveTagsTransform(phrase.phrase, (text) => text.toLowerCase()), index);
  };

  // Function to make all text uppercase
  const makeAllUppercase = () => {
    if (!phrase?.phrase) return;
    handleChange(preserveTagsTransform(phrase.phrase, (text) => text.toUpperCase()), index);
  };

  // Check if this textarea is active
  const isActive = active !== null && active === index;

  // Define styling variables based on component state
  let borderStyle = "solid";
  let borderWidth = "1px";
  let borderColor = "transparent";
  let bgColor = "transparent";
  let animation = "none";
  let boxShadow = "none";

  if (isRecentlySwapped) {
    // Apply the blink animation when items have just been swapped
    bgColor = "rgba(25, 118, 210, 0.1)";
    borderColor = "rgba(25, 118, 210, 0.5)";
    borderStyle = "solid";
    animation = "blink 0.8s ease-in-out";
  } else if (isActive) {
    borderColor = "rgba(0, 0, 0, 0.2)";
    bgColor = "rgba(25, 118, 210, 0.05)";
  } else if (dragInfo.isDraggedOver) {
    borderColor = "rgba(25, 118, 210, 0.8)";
    borderStyle = "solid";
    borderWidth = "2px";
    bgColor = "rgba(25, 118, 210, 0.05)";
    boxShadow = "0 0 8px rgba(25, 118, 210, 0.25)";
  } else if (isSelectedForDragging) {
    borderColor = "rgba(25, 118, 210, 0.3)";
    bgColor = "rgba(25, 118, 210, 0.05)";
  } else {
    borderColor = "rgba(0, 0, 0, 0.15)";
  }

  // Combined border string for the Box component
  const border = `${borderWidth} ${borderStyle} ${borderColor}`;

  // Define the keyframes for the blink animation
  const blinkKeyframes = `
    @keyframes blink {
      0% {
        background-color: rgba(25, 118, 210, 0.1);
        border-color: rgba(25, 118, 210, 0.4);
      }
      50% {
        background-color: rgba(25, 118, 210, 0.25);
        border-color: rgba(25, 118, 210, 0.8);
      }
      100% {
        background-color: rgba(25, 118, 210, 0.1);
        border-color: rgba(25, 118, 210, 0.4);
      }
    }
  `;

  // Determine if dragging from top or bottom
  const isDraggingFromTop =
    dragInfo.isDraggedOver &&
    dragInfo.dragSourceIndex !== null &&
    dragInfo.dragSourceIndex < index;
  const isDraggingFromBottom =
    dragInfo.isDraggedOver &&
    dragInfo.dragSourceIndex !== null &&
    dragInfo.dragSourceIndex > index;

  return (
    <>
      {/* Insert the keyframes for the blink animation */}
      {isRecentlySwapped && <style>{blinkKeyframes}</style>}

      {/* Show opening paragraph wrap tag on first item (not in KV — rendered by parent) */}
      {!isKV && paragraphWrapInfo.openingTag && index === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pl: 1,
            pb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontFamily: "monospace",
              color: "#666",
            }}
          >
            {compactTagAnnotation(paragraphWrapInfo.openingTag)}
          </Typography>
        </Box>
      )}

      {/* Show opening <ul> tag for UL+LI format on first item (not in KV) */}
      {!isKV && listWrapInfo.openingTag && index === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pl: 1,
            pb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontFamily: "monospace",
              color: "#666",
            }}
          >
            {compactTagAnnotation(listWrapInfo.openingTag)}
          </Typography>
        </Box>
      )}

      <Box
        ref={ref}
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          bgcolor: bgColor,
          borderRadius: "5px",
          border:
            !isDraggingFromTop &&
            !isDraggingFromBottom &&
            !isSelectedForDragging
              ? border
              : "none",
          paddingTop: isDraggingFromBottom ? "40px" : "0",
          paddingBottom: isDraggingFromTop ? "40px" : "0",
          transition:
            "border 0.2s ease, background-color 0.2s ease, padding 0.2s ease",
          animation: animation,
          boxShadow: boxShadow,
        }}
      >
        {/* Formatting tooltip that appears when focused */}
        {isActive && contentGroup.kind === "title" && (
          <Box
            sx={{
              display: readOnly ? "none" : "flex",
              position: "absolute",
              top: "-30px",
              right: "0",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 10,
              border: "1px solid #ddd",
            }}
          >
            <Tooltip title="Capitalize first letter">
              <Button
                size="small"
                variant="text"
                onClick={capitalizeFirstLetter}
                sx={{
                  minWidth: "32px",
                  padding: "2px",
                  textTransform: "none",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  <span style={{ fontSize: "0.9em" }}>A</span>
                  <span style={{ fontSize: "0.7em" }}>a</span>
                </Typography>
              </Button>
            </Tooltip>

            <Tooltip title="Lowercase">
              <Button
                size="small"
                variant="text"
                onClick={makeAllLowercase}
                sx={{
                  minWidth: "32px",
                  padding: "2px",
                  textTransform: "none",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  <span style={{ fontSize: "0.7em" }}>aa</span>
                </Typography>
              </Button>
            </Tooltip>

            <Tooltip title="Uppercase">
              <Button
                size="small"
                variant="text"
                onClick={makeAllUppercase}
                sx={{
                  minWidth: "32px",
                  padding: "2px",
                  textTransform: "none",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  <span style={{ fontSize: "0.7em" }}>AA</span>
                </Typography>
              </Button>
            </Tooltip>
          </Box>
        )}

        {/* Left annotation: bullet prefix + phrase wrap opening tag */}
        {hasLeftAnnotation && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              whiteSpace: "nowrap",
              pl: 1,
              pr: 0.5,
              gap: 0.5,
            }}
          >
            {hasPrefix && bulletPrefixInfo.text && (
              <Typography
                sx={{
                  fontSize: bulletPrefixInfo.isHtmlTag ? "0.7rem" : "1rem",
                  fontFamily: bulletPrefixInfo.isHtmlTag ? "monospace" : "inherit",
                  color: bulletPrefixInfo.isHtmlTag ? "#666" : "inherit",
                }}
              >
                {bulletPrefixInfo.text}
              </Typography>
            )}
            {!isKVValue && phraseWrapInfo.openingTag && (
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontFamily: "monospace",
                  color: "#666",
                }}
              >
                {compactTagAnnotation(phraseWrapInfo.openingTag)}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormattableInput
            readOnly={readOnly}
            spellCheck={true}
            lang="it"
            ref={textareaRef}
            id={`phrase-${phrase?.type}-${index}`}
            value={phrase?.phrase || ""}
            onChange={(val) => {
              handleChange(val, index);
            }}
            placeholder={placeholder}
            style={{
              border: "none",
              borderRadius: "0",
              backgroundColor: "transparent",
            }}
            onFocus={() => {
              setActive(index);
            }}
            onClick={() => {
              setActive(index);
            }}
          />
        </Box>

        {/* Right side: phrase wrap closing tag + status + drag handle */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexShrink: 0,
            whiteSpace: "nowrap",
            pl: 0.5,
            pr: 0.5,
            gap: 0.5,
          }}
        >
          {!isKVValue && phraseWrapInfo.closingTag && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                color: "#666",
              }}
            >
              {compactTagAnnotation(phraseWrapInfo.closingTag)}
            </Typography>
          )}

          {!user?.isClientRelated && phraseStatus && (
            <Tooltip
              title={
                phraseStatus === PhraseStatus.UNREVIEWED
                  ? "Not reviewed"
                  : phraseStatus === PhraseStatus.INTERNALLY_REVIEWED
                  ? "Internally reviewed"
                  : "Externally reviewed"
              }
            >
              <Circle
                sx={{
                  fontSize: "0.75rem",
                  color: phraseColor,
                  marginRight: readOnly ? "0.5rem" : "0",
                }}
              />
            </Tooltip>
          )}

          <Box
            ref={dragHandleRef}
            sx={{
              cursor: isKV ? "default" : "grab",
              mt: 0.7,
              display: isKV ? "none" : "block",
            }}
          >
            <DragIndicator
              sx={{
                fontSize: "1.25rem",
                cursor: "grab",
                display: readOnly ? "none" : "block",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Show closing </ul> tag for UL+LI format on last item (not in KV) */}
      {!isKV &&
        listWrapInfo.closingTag &&
        maxPhrases &&
        index === maxPhrases - 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: 1,
              pr: 0.5,
              pt: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                color: "#666",
              }}
            >
              {compactTagAnnotation(listWrapInfo.closingTag)}
            </Typography>
          </Box>
        )}

      {/* Show closing paragraph wrap tag on last item (not in KV) */}
      {!isKV &&
        paragraphWrapInfo.closingTag &&
        maxPhrases &&
        index === maxPhrases - 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: 1,
              pr: 0.5,
              pt: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                color: "#666",
              }}
            >
              {compactTagAnnotation(paragraphWrapInfo.closingTag)}
            </Typography>
          </Box>
        )}
    </>
  );
});
